import { GoogleClassroomClient } from "@/services/GoogleClassroom";
import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import { FirestoreClient } from "@/services/FirestoreClient";
import {
  ClassroomCourseWorkEntity,
  CourseWorkPupilProgress,
  UpsertCourseWorkPupilProgressArgs,
  upsertCourseWorkPupilProgressArgsSchema,
  UPDATABLE_SUBMISSION_STATES,
  PostSubmissionState,
  postSubmissionStateSchema,
} from "@/types/services";
import { OakGoogleClassroomException, ErrorSeverity } from "@/errors";

export class OakCourseWorkProgress {
  constructor(
    private readonly baseUrl: string,
    private readonly firestoreClient: FirestoreClient,
  ) {}

  /**
   * Retrieves a pupil's submission for a given courseWork item.
   * Uses the pupil's OAuth client (userId=me).
   * @param courseId - The Google Classroom course ID
   * @param courseWorkId - The courseWork item ID
   * @param pupilOAuthClient - An authenticated OAuth client for the pupil
   * @returns The submissionId from Google Classroom
   */
  public async getSubmissionId(
    courseId: string,
    courseWorkId: string,
    pupilOAuthClient: GoogleOAuthClient,
  ): Promise<string> {
    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      pupilOAuthClient,
    );
    const submission = await classroomClient.getStudentSubmission(
      courseId,
      courseWorkId,
    );
    if (!submission.id) {
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Student submission has no ID",
        "getSubmissionId",
        { courseId, courseWorkId },
      );
    }
    return submission.id;
  }

  /**
   * Persists pupil lesson progress to Firestore and, when the exit quiz is
   * newly completed, patches the Google Classroom submission grade using the
   * teacher's OAuth credentials.
   *
   * Grade sync is guarded by submission state: if the submission is already
   * TURNED_IN or RETURNED, the grade is not patched (mirrors OakPupilProgress behaviour).
   *
   * @param args - The pupil progress data to upsert
   * @param teacherOAuthClient - An OAuth client authenticated with the teacher's credentials
   * @returns The persisted progress record
   */
  public async upsertCourseWorkPupilProgress(
    args: UpsertCourseWorkPupilProgressArgs,
    teacherOAuthClient?: GoogleOAuthClient,
    courseWork?: ClassroomCourseWorkEntity,
    pupilOAuthClient?: GoogleOAuthClient,
  ): Promise<CourseWorkPupilProgress> {
    const validatedArgs = upsertCourseWorkPupilProgressArgsSchema.parse(args);

    const existing = await this.firestoreClient.getCourseWorkPupilProgress(
      validatedArgs.submissionId,
      validatedArgs.assignmentToken,
    );

    const now = new Date().toISOString();
    let progressData: CourseWorkPupilProgress = {
      ...existing,
      ...validatedArgs,
      gradeSubmitted: existing?.gradeSubmitted ?? false,
      resultsLinkAttached: existing?.resultsLinkAttached ?? false,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };

    await this.firestoreClient.upsertCourseWorkPupilProgress(progressData);

    const isExitQuizNewlyComplete =
      validatedArgs.exitQuiz?.isComplete && !existing?.exitQuiz?.isComplete;
    const gradeNotYetSubmitted = !existing?.gradeSubmitted;

    if (isExitQuizNewlyComplete && gradeNotYetSubmitted && teacherOAuthClient) {
      await this.submitGrade(
        validatedArgs,
        teacherOAuthClient,
        progressData,
        courseWork,
      );
      progressData = { ...progressData, gradeSubmitted: true };
    }

    // Attach the results page link to the student's submission on the first
    // progress save so teachers can review progress before hand-in.
    if (!existing?.resultsLinkAttached && pupilOAuthClient) {
      const resolved =
        courseWork ??
        (await this.firestoreClient.getClassroomCourseWork(
          validatedArgs.assignmentToken,
        ));
      if (resolved)
        return this.attachResultsLink(
          validatedArgs,
          resolved,
          pupilOAuthClient,
          progressData,
        );
    }

    return progressData;
  }

  private async attachResultsLink(
    args: UpsertCourseWorkPupilProgressArgs,
    courseWork: ClassroomCourseWorkEntity,
    pupilOAuthClient: GoogleOAuthClient,
    progressData: CourseWorkPupilProgress,
  ): Promise<CourseWorkPupilProgress> {
    try {
      const pupilClassroomClient = new GoogleClassroomClient(
        this.baseUrl,
        pupilOAuthClient,
      );
      const resultsUrl = new URL("/classroom/coursework/results", this.baseUrl);
      resultsUrl.searchParams.set("assignmentToken", args.assignmentToken);
      resultsUrl.searchParams.set("submissionId", args.submissionId);
      await pupilClassroomClient.addSubmissionLink(
        courseWork.courseId,
        courseWork.courseWorkId,
        args.submissionId,
        resultsUrl.toString(),
      );
      progressData = { ...progressData, resultsLinkAttached: true };
      await this.firestoreClient.upsertCourseWorkPupilProgress({
        ...progressData,
        updatedAt: new Date().toISOString(),
      });
    } catch (e) {
      console.warn(
        `Failed to attach results link on first save for submission ${args.submissionId}, continuing:`,
        e,
      );
    }
    return progressData;
  }

  /**
   * Checks the submission state and, if updatable, patches the grade.
   * Sets both draftGrade and assignedGrade as Google requires draftGrade
   * to exist before assignedGrade can be set.
   * Grade is normalised: (exitQuiz.grade / exitQuiz.numQuestions) * maxPoints.
   *
   * The courseWork entity is passed in from the facade (which already fetched it
   * to resolve teacher credentials) to avoid a redundant Firestore read and to
   * ensure the courseId/courseWorkId used for Google API calls come from the
   * trusted server-side record rather than client-supplied args.
   * Falls back to a Firestore lookup if the entity is not provided.
   */
  private async submitGrade(
    args: UpsertCourseWorkPupilProgressArgs,
    teacherOAuthClient: GoogleOAuthClient,
    progressData: CourseWorkPupilProgress,
    courseWork?: ClassroomCourseWorkEntity,
  ): Promise<void> {
    if (!courseWork) {
      const fetched = await this.firestoreClient.getClassroomCourseWork(
        args.assignmentToken,
      );
      if (!fetched) {
        console.warn(
          `CourseWork not found for assignmentToken=${args.assignmentToken}, skipping grade submission`,
        );
        return;
      }
      courseWork = fetched;
    }

    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      teacherOAuthClient,
    );

    // Use courseId/courseWorkId from the trusted Firestore entity rather than
    // client-supplied args — prevents a client from supplying a valid
    // assignmentToken for one assignment but IDs for another.
    const submission = await classroomClient.getStudentSubmissionById(
      courseWork.courseId,
      courseWork.courseWorkId,
      args.submissionId,
    );
    const state = postSubmissionStateSchema
      .catch(PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED)
      .parse(submission.state);
    const canUpdate = UPDATABLE_SUBMISSION_STATES.includes(state);

    if (!canUpdate) {
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Submission is not in an updatable state",
        "submitGrade",
        {
          submissionId: args.submissionId,
          submissionState: state,
        },
        undefined,
        ErrorSeverity.Warning,
      );
    }

    const grade = args.exitQuiz?.grade ?? 0;

    await classroomClient.patchStudentSubmission(
      courseWork.courseId,
      courseWork.courseWorkId,
      args.submissionId,
      grade,
      grade,
    );

    // Mark grade as submitted so we don't re-patch on subsequent progress updates
    await this.firestoreClient.upsertCourseWorkPupilProgress({
      ...progressData,
      gradeSubmitted: true,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Attaches a results page link to the submission, then turns it in.
   * The link must be added BEFORE turning in — Google does not allow modifying
   * attachments on a TURNED_IN submission. Link attachment failure is non-fatal
   * and is logged as a warning so turn-in still proceeds.
   */
  public async turnIn(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
    assignmentToken: string,
    pupilOAuthClient: GoogleOAuthClient,
  ): Promise<void> {
    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      pupilOAuthClient,
    );
    const resultsUrl = new URL("/classroom/coursework/results", this.baseUrl);
    resultsUrl.searchParams.set("assignmentToken", assignmentToken);
    resultsUrl.searchParams.set("submissionId", submissionId);
    try {
      await classroomClient.addSubmissionLink(
        courseId,
        courseWorkId,
        submissionId,
        resultsUrl.toString(),
      );
    } catch (e) {
      console.warn(
        `Failed to attach results link to submission ${submissionId}, proceeding with turn-in:`,
        e,
      );
    }

    await classroomClient.turnInStudentSubmission(
      courseId,
      courseWorkId,
      submissionId,
    );

    // Persist the TURNED_IN state to Firestore so the client can restore the
    // handed-in button state on page refresh without an extra Classroom API call.
    try {
      const existing = await this.firestoreClient.getCourseWorkPupilProgress(
        submissionId,
        assignmentToken,
      );
      if (existing) {
        await this.firestoreClient.upsertCourseWorkPupilProgress({
          ...existing,
          postSubmissionState: PostSubmissionState.TURNED_IN,
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.warn(
        `Failed to persist TURNED_IN state for submission ${submissionId}:`,
        e,
      );
    }
  }

  /**
   * Retrieves pupil progress for a given submission.
   * Does not require Google Classroom authentication.
   */
  public async getCourseWorkPupilProgress(
    submissionId: string,
    assignmentToken: string,
  ): Promise<CourseWorkPupilProgress | null> {
    return this.firestoreClient.getCourseWorkPupilProgress(
      submissionId,
      assignmentToken,
    );
  }
}
